import React, { useMemo } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Download, CreditCard, Smartphone, Star, Gift, RotateCcw, FileText, Banknote, CircleDollarSign } from 'lucide-react';
import { CurrencyDollarIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { formatDateInBusinessTZ } from '../utils/date';
import { useUrlTabState } from '../hooks/useUrlTabState';

type BalanceTransactionRow = {
  id: string;
  type: 'credit' | 'debit' | 'refund' | 'withdrawal';
  bucket: 'withdrawable' | 'non_withdrawable';
  amount_cents: number;
  note: string | null;
  source_ref_type: string | null;
  created_at: string;
};

type FilterTab = 'all' | 'credit' | 'debit' | 'refund' | 'withdrawal';

type HistoryRange = 'month' | 'year' | 'all';
const HISTORY_RANGES: readonly HistoryRange[] = ['month', 'year', 'all'];

type TransactionWithBalance = BalanceTransactionRow & { running_balance_cents: number };

function formatUsdFromCents(cents: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
}

const SOURCE_REF_ICON: Record<string, React.ReactElement> = {
  stripe_payment:          <CreditCard className="w-4 h-4" />,
  zelle_payment:           <Smartphone className="w-4 h-4" />,
  payment_cart_funding:    <FileText className="w-4 h-4" />,
  purchase_bonus:          <Star className="w-4 h-4" />,
  admin_grant:             <Gift className="w-4 h-4" />,
  admin_refund:            <RotateCcw className="w-4 h-4" />,
  invoice_payment:         <FileText className="w-4 h-4" />,
  invoice_payment_refund:  <RotateCcw className="w-4 h-4" />,
  stripe_withdrawal:       <CreditCard className="w-4 h-4" />,
  zelle_withdrawal:        <Smartphone className="w-4 h-4" />,
  cash_withdrawal:         <Banknote className="w-4 h-4" />,
};

const TYPE_FALLBACK_ICON: Record<BalanceTransactionRow['type'], React.ReactElement> = {
  credit:     <CircleDollarSign className="w-4 h-4" />,
  debit:      <FileText className="w-4 h-4" />,
  refund:     <RotateCcw className="w-4 h-4" />,
  withdrawal: <Banknote className="w-4 h-4" />,
};

function iconFor(tx: BalanceTransactionRow): React.ReactElement {
  if (tx.source_ref_type && tx.source_ref_type in SOURCE_REF_ICON) {
    return SOURCE_REF_ICON[tx.source_ref_type];
  }
  return TYPE_FALLBACK_ICON[tx.type];
}

const TYPE_LABEL: Record<BalanceTransactionRow['type'], string> = {
  credit: 'Credit',
  debit: 'Debit',
  refund: 'Refund',
  withdrawal: 'Withdrawal',
};

const FILTER_TABS: { id: FilterTab; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'credit', label: 'Credits' },
  { id: 'debit', label: 'Debits' },
  { id: 'refund', label: 'Refunds' },
  { id: 'withdrawal', label: 'Withdrawals' },
];

const FILTER_TAB_IDS: readonly FilterTab[] = FILTER_TABS.map(t => t.id);

function descriptionFor(tx: BalanceTransactionRow): string {
  const label = TYPE_LABEL[tx.type];
  if (tx.note) return tx.type === 'withdrawal' ? `${label} · ${tx.note}` : tx.note;
  if (tx.source_ref_type) {
    return `${label} · ${tx.source_ref_type.replace(/_/g, ' ')}`;
  }
  const bucketLabel = tx.bucket === 'withdrawable' ? 'Cash' : 'Bonus';
  return `${label} · ${bucketLabel}`;
}

function exportToCsv(rows: TransactionWithBalance[]): void {
  const headers = ['Date', 'Type', 'Bucket', 'Description', 'Amount', 'Running Balance'];
  const lines = rows.map((tx) => {
    const sign = tx.type === 'debit' || tx.type === 'withdrawal' ? '-' : '+';
    const amount = `${sign}${formatUsdFromCents(Math.abs(tx.amount_cents))}`;
    const runningBalance = formatUsdFromCents(tx.running_balance_cents);
    const date = formatDateInBusinessTZ(tx.created_at);
    const bucket = tx.bucket === 'withdrawable' ? 'Cash' : 'Bonus';
    const desc = descriptionFor(tx).replace(/,/g, ' ');
    return [date, TYPE_LABEL[tx.type], bucket, desc, amount, runningBalance].join(',');
  });

  const csv = [headers.join(','), ...lines].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'bhcs-cash-history.csv';
  a.click();
  URL.revokeObjectURL(url);
}

export function WalletHistory() {
  const { user, isLoading: isAuthLoading } = useAuth();
  const [activeFilter, setActiveFilter] = useUrlTabState<FilterTab>('type', FILTER_TAB_IDS, 'all');
  const [range, setRange] = useUrlTabState<HistoryRange>('range', HISTORY_RANGES, 'year');

  const { data: parentId } = useQuery<string | null>({
    queryKey: ['bhcs-cash-parent-id', user?.email],
    queryFn: async () => {
      if (!user?.email) return null;
      const { data, error } = await supabase
        .from('parents')
        .select('id')
        .eq('email', user.email)
        .maybeSingle();
      if (error) throw error;
      return data?.id ?? null;
    },
    enabled: !!user?.email,
  });

  const { data: accountId } = useQuery<string | null>({
    queryKey: ['bhcs-cash-account-id', parentId],
    queryFn: async () => {
      if (!parentId) return null;
      const { data, error } = await supabase
        .from('balance_accounts')
        .select('id')
        .eq('parent_id', parentId)
        .maybeSingle();
      if (error) throw error;
      return data?.id ?? null;
    },
    enabled: !!parentId,
  });

  const { data: transactions, isLoading: isTxLoading } = useQuery<BalanceTransactionRow[]>({
    queryKey: ['bhcs-cash-all-tx', accountId],
    queryFn: async () => {
      if (!accountId) return [];
      const { data, error } = await supabase
        .from('balance_transactions')
        .select('id, type, bucket, amount_cents, note, source_ref_type, created_at')
        .eq('account_id', accountId)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return (data ?? []) as BalanceTransactionRow[];
    },
    enabled: !!accountId,
  });

  const { startIso, endIso } = useMemo(() => {
    if (range === 'all') return { startIso: null as string | null, endIso: null as string | null };
    const now = new Date();
    if (range === 'month') {
      const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1, 0, 0, 0));
      const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1, 0, 0, 0));
      return { startIso: start.toISOString(), endIso: end.toISOString() };
    }
    const start = new Date(Date.UTC(now.getUTCFullYear(), 0, 1, 0, 0, 0));
    const end = new Date(Date.UTC(now.getUTCFullYear() + 1, 0, 1, 0, 0, 0));
    return { startIso: start.toISOString(), endIso: end.toISOString() };
  }, [range]);

  // Compute running balance on the full ledger (ascending), then reverse for display (newest first)
  const txWithBalance = useMemo<TransactionWithBalance[]>(() => {
    const rows = transactions ?? [];
    let running = 0;
    const withBalance = rows.map((tx) => {
      const delta = tx.type === 'debit' || tx.type === 'withdrawal' ? -tx.amount_cents : tx.amount_cents;
      running += delta;
      return { ...tx, running_balance_cents: running };
    });
    return withBalance.reverse();
  }, [transactions]);

  const filteredRows = useMemo<TransactionWithBalance[]>(() => {
    return txWithBalance.filter((tx) => {
      if (startIso || endIso) {
        const ts = new Date(tx.created_at).getTime();
        if (startIso && ts < new Date(startIso).getTime()) return false;
        if (endIso && ts >= new Date(endIso).getTime()) return false;
      }
      if (activeFilter !== 'all' && tx.type !== activeFilter) return false;
      return true;
    });
  }, [txWithBalance, activeFilter, startIso, endIso]);

  if (isAuthLoading) {
    return <div className="py-10 text-center text-gray-600">Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-6 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <Link
              to="/wallet"
              className="mr-3 text-gray-500 hover:text-gray-700"
              aria-label="Back to BHCS Cash"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <h1 className="text-lg sm:text-xl font-semibold text-gray-900 flex items-center">
              <CurrencyDollarIcon className="w-5 h-5 mr-2 text-gray-700" />
              BHCS Cash History
            </h1>
          </div>
          {txWithBalance.length > 0 && (
            <button
              type="button"
              onClick={() => exportToCsv(filteredRows)}
              className="inline-flex items-center px-3 py-2 rounded-md border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50"
              data-testid="wallet-history-export-csv"
            >
              <Download className="w-4 h-4 mr-1" />
              Export CSV
            </button>
          )}
        </div>

        {/* Date range + type filters */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="inline-flex rounded-lg border border-gray-200 bg-white overflow-hidden" data-testid="wallet-history-range-tabs">
            <button
              type="button"
              onClick={() => setRange('month')}
              data-testid="wallet-history-range-month"
              className={`px-3 py-1.5 text-sm focus:outline-none ${range === 'month' ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-50'} rounded-l-lg`}
            >
              Month
            </button>
            <button
              type="button"
              onClick={() => setRange('year')}
              data-testid="wallet-history-range-year"
              className={`px-3 py-1.5 text-sm focus:outline-none ${range === 'year' ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-50'}`}
            >
              Year
            </button>
            <button
              type="button"
              onClick={() => setRange('all')}
              data-testid="wallet-history-range-all"
              className={`px-3 py-1.5 text-sm focus:outline-none ${range === 'all' ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-50'} rounded-r-lg`}
            >
              All
            </button>
          </div>
        </div>

        {/* Type filter tabs */}
        <div className="flex gap-1 mb-4 bg-white rounded-lg border border-gray-200 p-1" data-testid="wallet-history-filter-tabs">
          {FILTER_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveFilter(tab.id)}
              data-testid={`wallet-history-filter-${tab.id}`}
              className={`flex-1 py-1.5 px-2 rounded-md text-xs sm:text-sm font-medium transition-colors ${
                activeFilter === tab.id
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Transaction list */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 divide-y divide-gray-100">
          {isTxLoading ? (
            <div className="p-6 text-center text-gray-600 text-sm">Loading...</div>
          ) : filteredRows.length === 0 ? (
            <div className="p-6 text-center text-gray-600 text-sm" data-testid="wallet-history-empty">
              {txWithBalance.length === 0
                ? 'No BHCS Cash activity yet.'
                : 'No transactions found for this period.'}
            </div>
          ) : (
            filteredRows.map((tx) => {
              const isDebit = tx.type === 'debit' || tx.type === 'withdrawal';
              const sign = isDebit ? '-' : '+';
              const amountStr = `${sign}${formatUsdFromCents(Math.abs(tx.amount_cents))}`;

              return (
                <div
                  key={tx.id}
                  className="flex items-center px-4 py-3 gap-3"
                  data-testid={`wallet-history-row-${tx.id}`}
                >
                  <span className="flex-shrink-0 text-gray-400" aria-label={TYPE_LABEL[tx.type]}>
                    {iconFor(tx)}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{descriptionFor(tx)}</p>
                    <p className="text-xs text-gray-500">{formatDateInBusinessTZ(tx.created_at)}</p>
                  </div>
                  <div className="flex-shrink-0 text-right">
                    <p className={`text-sm font-semibold ${isDebit ? 'text-red-600' : 'text-green-700'}`}>
                      {amountStr}
                    </p>
                    <p className="text-xs text-gray-500">{formatUsdFromCents(tx.running_balance_cents)}</p>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
